import { CDN_URL, WORKER_URL } from '../../config.js';
import { tokenManager } from '../../utils/TokenManager.js';
import { fetchCache } from '../../utils/fetchCache.js';
import { getCardAnimationEnabled, getNsfwEnabled } from '../../utils/userPrefs.js';

const privateCardObjectUrls = new Map();

export const isAnimatedCardExt = (ext) => ext === 'webm' || ext === 'mp4' || ext === 'gif';
export const isVideoCardExt = (ext) => ext === 'webm' || ext === 'mp4';

const normalizeExt = (value) => String(value || '').trim().toLowerCase();

export const resolveCardExtForPreference = (character, nsfwEnabled = getNsfwEnabled()) => {
  if (nsfwEnabled) return normalizeExt(character?.cen);
  return normalizeExt(character?.ce);
};

export const hasRankingCard = (character, nsfwEnabled = getNsfwEnabled()) => (
  Boolean(resolveCardExtForPreference(character, nsfwEnabled))
);

export const buildPublicCardStillUrl = (characterId, { nsfw = false } = {}) => (
  nsfw
    ? `${CDN_URL}/s/cards/${characterId}_n.webp`
    : `${CDN_URL}/s/cards/${characterId}.webp`
);

export const buildPublicCardVideoUrl = (characterId, ext, { nsfw = false } = {}) => (
  nsfw
    ? `${CDN_URL}/s/cards/${characterId}_n.${ext}`
    : `${CDN_URL}/s/cards/${characterId}.${ext}`
);

export const buildPrivateCardUrl = (characterId, { kind = 'still', ext = 'webp', nsfw = false } = {}) => {
  const params = new URLSearchParams({ kind });
  if (kind === 'video') params.set('ext', ext);
  if (nsfw) params.set('variant', 'n');
  return `${WORKER_URL}/private/cards/${characterId}?${params.toString()}`;
};

export const resolveRankingCardUrls = (character, { isPrivate = false, nsfwEnabled = getNsfwEnabled() } = {}) => {
  const nsfw = Boolean(nsfwEnabled);
  const ext = resolveCardExtForPreference(character, nsfw);
  if (!ext) return { stillUrl: null, videoUrl: null, needsAuth: false, ext: null, nsfw };
  if (isPrivate) {
    return {
      stillUrl: buildPrivateCardUrl(character.id, { kind: 'still', nsfw }),
      videoUrl: isAnimatedCardExt(ext) ? buildPrivateCardUrl(character.id, { kind: 'video', ext, nsfw }) : null,
      needsAuth: true,
      ext,
      nsfw
    };
  }
  return {
    stillUrl: buildPublicCardStillUrl(character.id, { nsfw }),
    videoUrl: isAnimatedCardExt(ext) ? buildPublicCardVideoUrl(character.id, ext, { nsfw }) : null,
    needsAuth: false,
    ext,
    nsfw
  };
};

export const fetchAuthedMediaObjectUrl = async (url) => {
  if (privateCardObjectUrls.has(url)) return privateCardObjectUrls.get(url);
  const token = await tokenManager.getValidAccessToken();
  if (!token) return null;
  const pending = (async () => {
    const blob = await fetchCache.fetchBlob(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return URL.createObjectURL(blob);
  })();
  privateCardObjectUrls.set(url, pending);
  try {
    return await pending;
  } catch {
    privateCardObjectUrls.delete(url);
    return null;
  }
};

export const isPrivateCharacter = (character) =>
  !!(character?.private || character?.rank === 'Private');

export const fetchRankingCardStillObjectUrl = async (character) => {
  const id = character?.id;
  if (id == null || String(id).trim() === '') return null;

  const nsfwEnabled = getNsfwEnabled();
  if (!hasRankingCard(character, nsfwEnabled)) return null;

  const urls = resolveRankingCardUrls(character, {
    isPrivate: isPrivateCharacter(character),
    nsfwEnabled
  });
  if (!urls.stillUrl) return null;
  if (urls.needsAuth) return fetchAuthedMediaObjectUrl(urls.stillUrl);
  return urls.stillUrl;
};

export const resolvePickerCardMedia = (character, {
  nsfwEnabled = getNsfwEnabled(),
  animate = getCardAnimationEnabled()
} = {}) => {
  if (character?.id == null || String(character.id).trim() === '') return null;
  if (!hasRankingCard(character, nsfwEnabled)) return null;

  const urls = resolveRankingCardUrls(character, {
    isPrivate: isPrivateCharacter(character),
    nsfwEnabled
  });
  if (isAnimatedCardExt(urls.ext)) {
    if (!urls.videoUrl) return null;
    return {
      kind: isVideoCardExt(urls.ext) ? 'video' : 'image',
      url: urls.videoUrl,
      needsAuth: urls.needsAuth,
      ext: urls.ext,
      freeze: !animate
    };
  }
  if (!urls.stillUrl) return null;
  return {
    kind: 'image',
    url: urls.stillUrl,
    needsAuth: urls.needsAuth,
    ext: urls.ext,
    freeze: false
  };
};

export const fetchPickerCardMedia = async (character, options) => {
  const spec = resolvePickerCardMedia(character, options);
  if (!spec) return null;
  const src = spec.needsAuth ? await fetchAuthedMediaObjectUrl(spec.url) : spec.url;
  if (!src) return null;
  return { ...spec, src };
};

export const createPickerCardMediaEl = ({ alt = '', kind = 'image' } = {}) => {
  if (kind === 'video') {
    const video = document.createElement('video');
    video.className = 'card-media';
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('aria-hidden', 'true');
    video.preload = 'metadata';
    video.poster = '/assets/images/kiss.webp';
    return video;
  }
  const img = document.createElement('img');
  img.className = 'card-media';
  img.alt = alt;
  img.decoding = 'async';
  img.loading = 'lazy';
  return img;
};

const freezeGifFirstFrame = (img) => {
  if (!(img instanceof HTMLImageElement) || !img.src) return;
  try {
    const width = img.naturalWidth || img.width;
    const height = img.naturalHeight || img.height;
    if (!width || !height) return;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(img, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      img.src = URL.createObjectURL(blob);
    }, 'image/webp');
  } catch {
    // Cross-origin stills stay animated rather than throwing.
  }
};

export const bindPickerCardMedia = (el, { src, kind, freeze, ext } = {}) => {
  if (!el || !src) return;
  if (kind === 'video' || el instanceof HTMLVideoElement) {
    el.muted = true;
    el.playsInline = true;
    el.preload = freeze ? 'metadata' : 'auto';
    el.loop = !freeze;
    if (freeze) {
      const freezeFrame = () => {
        el.pause();
        try {
          el.currentTime = 0;
        } catch {
          // Some sources reject seeking before more data arrives.
        }
      };
      el.addEventListener('loadeddata', freezeFrame, { once: true });
    }
    el.src = src;
    if (!freeze) void el.play?.().catch(() => {});
    return;
  }
  if (freeze && ext === 'gif') {
    if (!src.startsWith('blob:') && !src.startsWith('data:')) {
      el.crossOrigin = 'anonymous';
    }
    el.addEventListener('load', () => freezeGifFirstFrame(el), { once: true });
  }
  el.src = src;
};

export const releasePickerCardMedia = (root) => {
  if (!root?.querySelectorAll) return;
  root.querySelectorAll('video.card-media').forEach((video) => {
    video.pause();
    video.removeAttribute('src');
    video.load();
  });
};
